import React, { useState } from 'react';
import { ArrowLeft, BookMarked, Lightbulb, Target, CreditCard, RotateCcw } from 'lucide-react';
import { useVocabulary } from '../context/VocabularyContext';
import { srsManager } from '../utils/srsManager';

export function SentenceSubcategoriesGrid() {
  const { state, dispatch, actions } = useVocabulary();
  const { selectedStage, sentencesData } = state;
  const [resetConfirm, setResetConfirm] = useState(null);

  const handleBackToStages = () => {
    dispatch({ type: actions.SET_CURRENT_VIEW, payload: 'sentence-categories' });
  };

  const handleResetSRS = (poolCode) => {
    setResetConfirm(poolCode);
  };

  const confirmResetSRS = (poolCode) => {
    const success = srsManager.resetData('sentences', poolCode);
    if (success) {
      alert(`Dane SRS dla puli ${poolCode} zostały zresetowane! Możesz teraz ćwiczyć wszystkie zdania z tej puli od nowa.`);
    } else {
      alert('Wystąpił błąd podczas resetowania danych SRS.');
    }
    setResetConfirm(null);
  };

  const cancelReset = () => {
    setResetConfirm(null);
  };

  const handleStartSentenceExercise = async (poolCode) => {
    try {
      console.log(`Loading sentences from pool: ${poolCode}`);
      
      // Load sentences from pool file
      const response = await fetch(`/data/sentences/${poolCode}.json`);
      if (!response.ok) {
        throw new Error(`Failed to load pool ${poolCode}`);
      }
      
      const poolData = await response.json();
      const sentences = poolData.sentences || [];
      
      // Initialize SRS records for all items in the pool (if not already initialized)
      srsManager.initializePoolItems('sentences', poolCode, sentences);
      
      // Filter sentences through SRS BEFORE passing to session
      // This ensures we have proper id mappings that won't break after shuffling
      const availableSentences = srsManager.getAvailableSentencesForPool('sentences', poolCode, sentences);
      
      if (availableSentences.length === 0) {
        alert(`🎉 Gratulacje!\n\nWszystkie zdania z tej puli są aktualnie opanowane.\nSprawdź statystyki lub poczekaj na kolejną powtórkę.`);
        return;
      }
      
      console.log(`Loaded ${sentences.length} sentences, ${availableSentences.length} available for review`);
      
      // Start session with available sentences only (always flashcards mode)
      // Pass full SRS objects with id and item for proper tracking
      dispatch({ 
        type: actions.START_SENTENCE_SESSION, 
        payload: { 
          stage: selectedStage, 
          poolCode: poolCode,
          sentences: availableSentences, // Pass full SRS objects (with id, item, srsData)
          mode: 'flashcards' // Always use flashcards mode
        } 
      });
    } catch (error) {
      console.error(`Error loading pool ${poolCode}:`, error);
      alert(`Błąd podczas ładowania zdań: ${error.message}`);
    }
  };

  const getStageName = (stage) => {
    if (!sentencesData || !sentencesData.stage_categories) {
      return `Etap ${stage}`;
    }
    const stageCategories = sentencesData.stage_categories;
    return stageCategories[stage]?.name || `Etap ${stage}`;
  };

  const getPoolIcon = (poolNumber) => {
    const icons = {
      '01': BookMarked,
      '02': Lightbulb, 
      '03': Target
    };
    return icons[poolNumber] || BookMarked;
  };

  // Get pools for selected stage from sentences data
  const getPoolsForStage = (stage) => {
    if (!sentencesData || !sentencesData.stage_categories || !sentencesData.pools) {
      return [];
    }
    
    const stageCategories = sentencesData.stage_categories;
    const pools = sentencesData.pools;
    const stageData = stageCategories[stage];
    
    if (!stageData || !stageData.pools) {
      return [];
    }

    return stageData.pools.map(poolCode => {
      const poolData = pools[poolCode];
      if (!poolData) return null;
      
      return {
        poolCode,
        name: poolData.name,
        description: poolData.description,
        difficulty: poolData.difficulty,
        sentenceCount: poolData.sentence_count,
        color: getPoolColor(poolCode)
      };
    }).filter(Boolean);
  };

  const getPoolColor = (poolCode) => {
    // Assign colors based on pool number
    const poolNumber = poolCode.split('-')[2];
    const colors = {
      '01': 'blue',
      '02': 'green',
      '03': 'orange'
    };
    return colors[poolNumber] || 'blue';
  };

  const pools = getPoolsForStage(selectedStage);

  return (
    <div className="space-y-6">
      {/* Navigation Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={handleBackToStages}
          className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          title="Powrót do wyboru etapu"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            {getStageName(selectedStage)}
          </h1>
          <p className="text-gray-600">
            Wybierz pulę zdań do ćwiczenia
          </p>
        </div>
      </div>

      {/* Pools Grid */}
      <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-6">
        {pools.map((pool, index) => {
          const poolNumber = String(index + 1).padStart(2, '0');
          const IconComponent = getPoolIcon(poolNumber);
          
          const colorClasses = {
            blue: 'bg-blue-50 hover:bg-blue-100 border-blue-200 hover:border-blue-300',
            green: 'bg-green-50 hover:bg-green-100 border-green-200 hover:border-green-300',
            orange: 'bg-orange-50 hover:bg-orange-100 border-orange-200 hover:border-orange-300'
          };
          
          const iconColorClasses = {
            blue: 'bg-blue-600',
            green: 'bg-green-600', 
            orange: 'bg-orange-600'
          };

          const difficultyColors = {
            'Łatwy': 'bg-green-100 text-green-700',
            'Średni': 'bg-yellow-100 text-yellow-700',
            'Trudny': 'bg-red-100 text-red-700'
          };
          
          return (
            <div
              key={pool.poolCode}
              className={`p-6 ${colorClasses[pool.color]} border-2 rounded-xl transition-all duration-200`}
            >
              {/* Pool Header */}
              <div className="flex items-center mb-4">
                <div className={`p-3 ${iconColorClasses[pool.color]} text-white rounded-lg`}>
                  <IconComponent size={24} />
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-lg font-bold text-gray-800">
                    {pool.name}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {pool.poolCode}
                  </p>
                </div>
              </div>
              
              {/* Pool Description */}
              <p className="text-gray-600 mb-4">
                {pool.description}
              </p>
              
              {/* Pool Stats */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-600">
                    📝 {pool.sentenceCount} zdań
                  </span>
                  <span className={`text-xs px-2 py-1 rounded-full ${difficultyColors[pool.difficulty]}`}>
                    {pool.difficulty}
                  </span>
                </div>
              </div>

              {/* Pool SRS Stats */}
              <div className="flex justify-between text-xs text-gray-600 border-t pt-2 mb-2">
                {(() => {
                  const srsStats = require('../utils/srsManager').srsManager.getStats('sentences', pool.poolCode);
                  return <>
                    <div><span className="font-medium">{srsStats.total_items}</span> w puli</div>
                    <div><span className="font-medium">{srsStats.due_for_review}</span> dostępnych</div>
                  </>;
                })()}
              </div>
              
              {/* Start Button */}
              <button
                onClick={() => handleStartSentenceExercise(pool.poolCode)}
                className={`w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center group mb-2`}
              >
                <CreditCard size={18} className="mr-2 group-hover:scale-110 transition-transform" />
                Rozpocznij fiszki
              </button>
              
              {/* Reset SRS Button */}
              <button
                onClick={() => handleResetSRS(pool.poolCode)}
                className="w-full bg-orange-100 hover:bg-orange-200 text-orange-700 font-medium py-2 px-4 rounded-lg transition-all duration-200 flex items-center justify-center group text-sm"
                title="Resetuj postępy SRS dla tej puli"
              >
                <RotateCcw size={16} className="mr-2 group-hover:rotate-180 transition-transform" />
                Resetuj postępy
              </button>
            </div>
          );
        })}
      </div>

      {/* No pools message */}
      {pools.length === 0 && (
        <div className="text-center py-8">
          <div className="text-gray-500">
            <span className="text-4xl block mb-2">📚</span>
            Brak dostępnych pul dla tego etapu
          </div>
        </div>
      )}

      {/* Reset Confirmation Dialog */}
      {resetConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4 text-orange-600">⚠️ Resetuj postępy SRS</h3>
            <p className="mb-6 text-gray-700">
              Czy na pewno chcesz zresetować postępy SRS dla puli <strong>{resetConfirm}</strong>?
              <br /><br />
              <span className="text-orange-600">Ta akcja jest nieodwracalna!</span> Wszystkie zdania z tej puli będą ponownie dostępne do nauki.
            </p>
            <div className="flex gap-3">
              <button 
                onClick={() => confirmResetSRS(resetConfirm)}
                className="flex-1 py-2 px-4 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium"
              >
                🔄 Resetuj
              </button>
              <button 
                onClick={cancelReset}
                className="flex-1 py-2 px-4 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Anuluj
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
